import { useState, useRef } from "react";
import Tooltip from "@mui/material/Tooltip";
import PropTypes from "prop-types";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import { useAuth0 } from "@auth0/auth0-react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { exportToJson, permissionChecker } from "helpers/common";
import ConfirmationDialog from "layouts/pages/users/reports/components/Reviews/RemoveUserDialog";
import { RecoverFlowDialog } from "../RecoverFlowDialog";
import { PERMISSIONS } from "constants/portal";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { fetchFlows } from "redux/actions/flowDesigner/flowsActionCreator";
import { updateFlow } from "api/FlowDesignerApis";
import { updatePortalFlow } from "redux/actions/portal/flowsActionCreator";
import { updateFlowStatus } from "api/FlowsApi";
import { useFlowPermissions } from "hooks/useFlowPermissions";
import { DeleteFlow } from "./delete";
import { DeleteFlowPermanently } from "../DeleteFlowPermanently";
import { useFlowUsage } from "hooks/useFlowUsage";
import { deleteResourceDependency } from "redux/actions/portal/resourceDependenciesActionCreator";
import { getResourceDependencyContextId } from "helpers/resourceDependencies";

function FlowMenu({ item = {}, getFlows, flows, setOpenUsersModal, setOpenUsersModalUnshare, setSelectedUserFlow, setTitleName, actionForRF, users, userPermissions, handleCloneFlow, onlyViewAndRemove }) {
   const [flowMenu, setFlowMenu] = useState(null);
   const { user } = useAuth0();
   const [showRecoverFlowDialog, setShowRecoverFlowDialog] = useState(false);
   const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
   const [openConfirmationActivateDialog, setOpenConfirmationActivateDialog] = useState(false);
   const tabMap = useRef(new Map());
   const navigate = useNavigate();
   const dispatch = useDispatch();
   const { invitedUsers } = useFlowUsage(item);

   const { canEdit, canShare, canRetrieve, canDelete, canActivate, canDeactivate, canExport, canClone } = useFlowPermissions(item);

   const handleFlowOpenMenu = (event) => {
      event.preventDefault();
      setFlowMenu(event.currentTarget);
   };

   const handleFlowMenuClose = () => {
      setFlowMenu(null);
   };

   const handleCloseConfirmationDialog = () => {
      setOpenConfirmationDialog(false);
   };

   const handleCloseConfirmationActivateDialog = () => {
      setOpenConfirmationActivateDialog(false);
   };

   const handleEdit = () => {
      handleFlowMenuClose();

      if (window.location.href.includes("designer")) {
         return;
      }

      let tab = tabMap.current.get(item.id);
      if (!tab || tab.closed) {
         tab = window.open(`/designer/${item.id}`, item.id);
         tabMap.current.set(item.id, tab);
      } else {
         tab.focus();
      }
   };

   const handleShare = () => {
      setSelectedUserFlow(flows?.find((flow) => flow.id === item.id));
      setOpenUsersModal(true);
      handleFlowMenuClose();
      setTitleName("Share");
   };

   const handleUnshare = () => {
      setSelectedUserFlow(flows?.find((flow) => flow.id === item.id));
      setOpenUsersModalUnshare(true);
      handleFlowMenuClose();
      setTitleName("Unshare");
   };

   const handleRecover = () => {
      handleFlowMenuClose();
      setShowRecoverFlowDialog(true);
   };

   const handleRemove = async () => {
      try {
         const integrations = item.integrations ? JSON.parse(item.integrations) : [];
         const updatedIntegrations = integrations.filter((id) => id !== item.integrationId);

         const { id, created_date_time, updated_date_time, tenant_id, userAccessType, integrationId, ...restFlow } = item;

         const updatedFlow = {
            ...restFlow,
            integrations: JSON.stringify(updatedIntegrations)
         };

         const response = await updateFlow(user?.stripeId, item.id, updatedFlow, false);

         if (!response) {
            toast.error(`Unable to remove flow from integration - ensure that an instance of the cxf-executor is running and that the Flow Designer is connected to it. Then, reload and try again.`, {
               autoClose: 5000,
               pauseOnHover: true
            });
            return;
         }

         dispatch(updatePortalFlow({ ...updatedFlow, id: item.id }));
         dispatch(
            deleteResourceDependency({
               tenantId: user?.stripeId,
               sourceId: item.id,
               targetId: item.integrationId,
               sourceResourceType: "flow",
               targetResourceType: "integration",
               contextId: getResourceDependencyContextId(item.id, "integration")
            })
         );

         if (getFlows) getFlows(item.id);

         toast.success("Flow removed from integration.");
         handleFlowMenuClose();
      } catch (error) {
         toast.error("Failed to remove flow from integration.");
         console.log(error);
      }
   };

   const handleDeactivate = () => {
      setOpenConfirmationDialog(true);
      handleFlowMenuClose();
   };

   const handleActivate = () => {
      setOpenConfirmationActivateDialog(true);
      handleFlowMenuClose();
   };

   const handleConfirmDeactivation = async () => {
      try {
         const response = await updateFlowStatus(user?.stripeId, item.id, "test");
         handleCloseConfirmationDialog();

         if (response?.errors) {
            console.log(response.errors);
            toast.error("Flow has not been set to draft mode");
            return;
         }
         toast.success("Flow has been set to draft mode successfully!");
      } catch (error) {
         console.log(error);
      }
   };

   const handleConfirmActivation = async () => {
      try {
         const response = await updateFlowStatus(user?.stripeId, item.id, "active");
         handleCloseConfirmationActivateDialog();

         if (response?.errors) {
            console.log(response.errors);
            toast.error("Flow has not been activated");
            return;
         }
         toast.success("Flow has been activated successfully!");
      } catch (error) {
         console.log(error);
      }
   };

   const handleRetrievalOnDatabase = async () => {
      const { created_date_time, id, tenant_id, userAccessType, updated_date_time, ...restFlow } = item;

      try {
         const response = await updateFlow(user?.stripeId, item.id, { ...restFlow, trashed: false }, false);
         dispatch(updatePortalFlow({ ...restFlow, trashed: false, id }));
         setShowRecoverFlowDialog(false);

         if (response?.errors) {
            console.log(response.errors);
            toast.error(`Unable to save recovered flow - ensure that an instance of the cxf-executor is running and that the Flow Designer is connected to it. Then, reload and try again.`, {
               autoClose: 5000,
               pauseOnHover: true
            });
            return;
         }

         dispatch(fetchFlows(user?.stripeId));

         toast.success("Flow has been recovered successfully!");
      } catch (error) {
         console.log(error);
      }
   };

   const handleOpenFlowDetail = () => {
      navigate(`/flow/${item.id}`);
   };

   const canShareFlow = permissionChecker(userPermissions, PERMISSIONS.SHARE_ANY_WORKFLOWS) && !item.trashed && users?.length > 1 && users.length - 1 > invitedUsers.length;

   const canUnshareFlow = permissionChecker(userPermissions, PERMISSIONS.SHARE_ANY_WORKFLOWS) && !item.trashed && invitedUsers.length > 0;

   const isOwnerAndCanShare = item.created_by_id === user?.cxfUserId && !item.trashed && users?.length > 1 && users.length - 1 > invitedUsers.length;

   const isOwnerAndCanUnshare = item.created_by_id === user?.cxfUserId && !item.trashed && invitedUsers.length > 0;

   const renderMenu = (state, close) => {
      if (onlyViewAndRemove) {
         return (
            <Menu anchorEl={state} anchorOrigin={{ vertical: "top", horizontal: "left" }} transformOrigin={{ vertical: "top", horizontal: "right" }} open={Boolean(state)} onClose={close} keepMounted>
               <MenuItem onClick={handleOpenFlowDetail}>View Details</MenuItem>
               <MenuItem onClick={handleRemove}>Remove</MenuItem>
            </Menu>
         );
      }

      return (
         <Menu anchorEl={state} anchorOrigin={{ vertical: "top", horizontal: "left" }} transformOrigin={{ vertical: "top", horizontal: "right" }} open={Boolean(state)} onClose={close} keepMounted>
            {!actionForRF ? (
               <Box>
                  <MenuItem onClick={handleOpenFlowDetail}>View Details</MenuItem>
                  <MenuItem onClick={() => (canClone ? handleCloneFlow(true, item) : null)} disabled={!canClone}>
                     Clone
                  </MenuItem>

                  {canEdit && (
                     <MenuItem onClick={handleEdit} disabled={item.trashed}>
                        Edit
                     </MenuItem>
                  )}

                  {canExport && (
                     <MenuItem
                        onClick={() => {
                           exportToJson(item);
                           handleFlowMenuClose();
                        }}
                     >
                        Export
                     </MenuItem>
                  )}

                  {canShare && (
                     <MenuItem onClick={handleShare} disabled={!canShareFlow && !isOwnerAndCanShare}>
                        Share
                     </MenuItem>
                  )}

                  {canShare && (
                     <MenuItem onClick={handleUnshare} disabled={!canUnshareFlow && !isOwnerAndCanUnshare}>
                        Unshare
                     </MenuItem>
                  )}

                  {!item.trashed && item.shared_status === "active" && canDeactivate && <MenuItem onClick={handleDeactivate}>Set to Draft Mode</MenuItem>}

                  {!item.trashed && item.shared_status !== "active" && canActivate && <MenuItem onClick={handleActivate}>Activate</MenuItem>}

                  {item.trashed ? (
                     <>
                        {canRetrieve && <MenuItem onClick={handleRecover}>Recover</MenuItem>}
                        {canDelete && <DeleteFlowPermanently fromActions callback={handleFlowMenuClose} item={item} />}
                     </>
                  ) : (
                     canDelete && <DeleteFlow handleFlowMenuClose={handleFlowMenuClose} canDelete={canDelete} item={item} invitedUsers={invitedUsers} />
                  )}
               </Box>
            ) : (
               <Box>
                  {canEdit && (
                     <MenuItem onClick={handleEdit} disabled={item.trashed}>
                        Edit
                     </MenuItem>
                  )}

                  {canShare && (
                     <MenuItem onClick={handleShare} disabled={!(permissionChecker(userPermissions, PERMISSIONS.SHARE_ANY_WORKFLOWS) && !item.trashed) && !(item.created_by_id === user?.cxfUserId && !item.trashed)}>
                        Share
                     </MenuItem>
                  )}

                  {item.trashed ? canRetrieve && <MenuItem onClick={handleRecover}>Recover</MenuItem> : canDelete && <DeleteFlow handleFlowMenuClose={handleFlowMenuClose} canDelete={canDelete} item={item} />}
               </Box>
            )}
         </Menu>
      );
   };

   return (
      <>
         <Tooltip title="More Actions" placement="top">
            <Icon fontSize="default" onClick={handleFlowOpenMenu} sx={{ cursor: "pointer" }}>
               more_horiz_icon
            </Icon>
         </Tooltip>
         {renderMenu(flowMenu, handleFlowMenuClose)}
         <RecoverFlowDialog open={showRecoverFlowDialog} onAccept={handleRetrievalOnDatabase} onClose={() => setShowRecoverFlowDialog(false)} flow={item} />
         <ConfirmationDialog
            open={openConfirmationDialog}
            onClose={handleCloseConfirmationDialog}
            onAccept={handleConfirmDeactivation}
            acceptButtonText="Set to Draft Mode"
            dialogTitle="Set to Draft Mode flow"
            dialogContent={[`Do you really want to set to Draft Mode the `, item.name]}
            acceptButtonColor="info"
         />
         <ConfirmationDialog
            open={openConfirmationActivateDialog}
            onClose={handleCloseConfirmationActivateDialog}
            onAccept={handleConfirmActivation}
            acceptButtonText="Activate"
            dialogTitle="Activate Flow"
            dialogContent={[`Do you really want to activate the `, item.name]}
            acceptButtonColor="info"
         />
      </>
   );
}

FlowMenu.propTypes = {
   flowId: PropTypes.string
};

export default FlowMenu;
